// The app noticing, and saying so.
//
// This is the only place a rung is ever mentioned to an operator, and it
// says one thing at a time. There is no menu of features here and no
// settings page anywhere in this app: a tool appears at the moment the
// work asks for it, or it does not exist.
//
// Dismissing is real. It is not "remind me later" — the offer goes away
// and comes back the next time the need genuinely recurs, which is a
// count of occurrences rather than a clock (see `ops/ladder.ts`).

import type { Offer, RungEvidence } from "@wayfind/core";
import { Panel } from "./Bits";
import { dateWords } from "../ops/words";

export function OfferBanner({
  offer,
  onAccept,
  onDismiss
}: {
  offer: Offer;
  onAccept(): void;
  onDismiss(): void;
}): JSX.Element {
  return (
    <div className="offer" role="status">
      <span className="offer__title">{offer.title}</span>
      <p className="offer__body">{offer.body}</p>
      <div className="offer__actions">
        <button type="button" className="button button--small button--primary" onClick={onAccept}>
          {offer.kind === "unlock" ? "Show me" : "Yes, tuck it away"}
        </button>
        <button type="button" className="button button--small button--ghost" onClick={onDismiss}>
          {offer.kind === "unlock" ? "Not now" : "No, keep it"}
        </button>
      </div>
    </div>
  );
}

/**
 * What this console has worked out about the operation, and from what.
 *
 * Kept behind a toggle and out of the way, because it is the app talking
 * about itself. But it is worth being able to look at: an operator who
 * cannot see why the vocabulary changed is being managed rather than
 * helped, and every line here names the evidence.
 */
export function LadderEvidence({ evidence }: { evidence: RungEvidence[] }): JSX.Element {
  return (
    <Panel eyebrow="How big this app currently is" tone="quiet">
      <div className="rows">
        {evidence.map(entry => (
          <div key={entry.rung} className="rowcard">
            <div className="rowcard__main">
              <span className="rowcard__name">
                {entry.reached ? entry.why : NOT_YET[entry.rung] ?? "Not yet."}
              </span>
              {entry.reached && entry.atUnixSeconds ? (
                <span className="rowcard__sub">{dateWords(entry.atUnixSeconds)}</span>
              ) : null}
            </div>
            <span className={`chip${entry.reached ? " chip--settled" : ""}`}>
              <span className="chip__dot" aria-hidden="true" />
              {entry.reached ? "in use" : "not needed"}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

const NOT_YET: Record<number, string> = {
  1: "Everything so far has had one address on it.",
  2: "One phone has done all of it.",
  3: "Nothing has gone out on the same places three days inside a fortnight.",
  4: "There has always been somebody free.",
  5: "Not enough phones for a mesh of your own to be worth anything."
};
